import styled from "styled-components";
import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useSearchParams } from "react-router-dom";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import { daysUntil, numberWithCommas } from "modules/functions";
import ButtonSecondary from "components/buttons/ButtonSecondary";
import MessageBox from "components/other/MessageBox";

import adPlanIcon from "assets/img/icons/icons8-web-advertising.svg";
import bronzeCheckmarkIcon from "assets/img/icons/checkmark-bronze.svg";
import silverCheckmarkIcon from "assets/img/icons/checkmark-silver.svg";
import goldCheckmarkIcon from "assets/img/icons/checkmark-gold.svg";
import featureTickIcon from "assets/img/icons/icons8-feature-tick.svg";
import bankCardIcon from "assets/img/icons/icons8-bank-card-dollar.svg";
import stripeButtonImage from "assets/img/accepting-cards.svg";
import coinsIcon from "assets/img/icons/coins-icon.svg";
import arrowIcon from "assets/img/icons/icons8-arrow.svg";
import calendarIcon from "assets/img/icons/calendar-icon.svg";

const ReviewAdPlanWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const ReviewAdPlan = styled.div`
  position: relative;
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const BoxTitle = styled.div`
  width: 100%;

  line-height: 130%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
`;

const TitleIcon = styled.div`
  display: inline-block;
  padding-right: 5px;

  transform: translateY(4.5px);

  width: 26px;
  min-width: 26px;
`;

const Data = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DataTitle = styled.div`
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const DataRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const DataIconBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DataIcon = styled.div`
  width: ${(props) => (props.width ? props.width : "20px")};
  min-width: ${(props) => (props.width ? props.width : "20px")};
  height: ${(props) => (props.width ? props.width : "20px")};
`;

const DataText = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
`;

const Features = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const FeaturesTitle = styled.div`
  font-size: 16px;
  font-weight: 500;

  @media (max-width: 800px) {
    font-size: 17px;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Feature = styled.div`
  display: flex;
  gap: 5px;
`;

const FeatureIcon = styled.div`
  width: 20px;
  min-width: 20px;

  transform: ${(props) =>
    props.translate ? `translateY(${props.translate})` : "none"};

  display: inline-block;
`;

const FeatureText = styled.div`
  font-size: 16px;
  line-height: 130%;

  transform: ${(props) =>
    props.translate ? `translateY(${props.translate})` : "none"};
`;

const FeatureBadgeIcon = styled.div`
  width: 20px;
  min-width: 20px;
  padding: 0 3px;

  transform: translateY(5px);

  display: inline-block;
`;

const Bold = styled.span`
  font-weight: 500;
`;

const ChargeInfoBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_SECONDARY};
  margin-top: 2px;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  gap: 14px;
`;

const ChargeInfoBoxIcon = styled.div`
  width: 36px;
  min-width: 36px;

  transform: translateY(-1px);
`;

const ChargeInfoBoxText = styled.div`
  line-height: 130%;
`;

const PaymentSection = styled.div`
  margin-bottom: 10px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const Text = styled.div`
  line-height: 125%;
`;

const PaymentButtonBox = styled.div`
  margin-top: 10px;

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
    gap: 9px;
  }
`;

const PaymentButtonWrap = styled.div``;

const PaymentButton = styled.div`
  box-sizing: border-box;
  padding: 11px 25px;
  width: 100%;

  font-family: "Roboto", sans-serif;

  background: ${(props) => (props.disabled ? "#cdcdcd" : "#fff")};
  border-radius: 21px;
  border: 0;

  transition: 0.2s;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 7px;

  ${(props) =>
    !props.disabled &&
    `&:hover {
    cursor: pointer;

    color: #fff;
  }`}
`;

const PaymentButtonText = styled.div`
  color: ${css.DARK_BLUE};
  font-weight: 500;
  font-size: 16px;

  white-space: nowrap;
`;

const MethodImage = styled.div`
  height: 20px;

  transform: translateY(-2px);
`;

const PaymentText = styled.div`
  font-weight: 400;
  line-height: 1;
  white-space: nowrap;
`;

const GoldPlanCoinsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const CoinsSelection = styled.div`
  margin: 10px 0 5px;
  padding: 0 40px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${globalConstants.switchMobile}) {
    margin: 5px 0 0 0;
    padding: 0 10px;

    flex-direction: column;
    align-items: flex-start;
  }
`;

const CoinsSelectionOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  &:hover {
    cursor: pointer;
  }

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: row-reverse;
    gap: 10px;
  }
`;

const CoinsSelectionOptionValue = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const CoinsSelectionOptionIcon = styled.div`
  width: 18px;
  min-width: 18px;

  transform: translateY(1px);
`;

const CoinsSelectionOptionText = styled.div`
  font-weight: ${(props) => (props.selected ? "500" : "300")};
`;

const CoinsSelectionCircleBox = styled.div`
  width: 100%;

  display: flex;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
  }
`;

const CoinsSelectionOptionCircle = styled.div`
  width: 18px;
  height: 18px;

  border-radius: 8px;
  background-color: ${(props) => {
    if (props.filled) return css.BRAND_GREEN;
    if (props.selected) return css.BRAND_YELLOW;
    else return css.PAGE_BACKGROUND;
  }};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const CoinsSelectionOptionInnerCircle = styled.div`
  width: 12px;
  height: 12px;

  border-radius: 7px;
  background-color: ${(props) => {
    if (props.filled) return css.BRAND_GREEN;
    else return css.PAGE_BACKGROUND;
  }};
`;

const CoinsSelectionCircleBoxBar = styled.div`
  height: 6px;
  width: calc(50% - 9px);

  background-color: ${(props) => {
    if (props.transparent) return "transparent";
    if (props.filled) return css.BRAND_GREEN;
    else return css.PAGE_BACKGROUND;
  }};
`;

const CoinsSelectionCircleBoxBarMobile = styled.div`
  width: 6px;
  height: 10px;

  background-color: ${(props) => {
    if (props.transparent) return "transparent";
    if (props.filled) return css.BRAND_GREEN;
    else return css.PAGE_BACKGROUND;
  }};
`;

const CoinsSelectionBar = styled.div`
  height: 6px;

  background-color: ${(props) => {
    if (props.filled) return css.BRAND_GREEN;
    else return css.PAGE_BACKGROUND;
  }};

  transform: translateY(15px);

  flex: 1;
`;

const ChangePlanBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_SECONDARY};
  margin-top: 2px;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const ChangePlanTitle = styled.div`
  font-weight: 500;
`;

const ChangePlanText = styled.div`
  line-height: 130%;
`;

const ChangePlanCompensation = styled.div`
  display: flex;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
  flex-wrap: wrap;
`;

const ChangePlanCoinsIcon = styled.div`
  display: inline-block;
  padding-right: 4px;

  width: 20px;
  min-width: 20px;
  height: 20px;

  transform: translateY(4px);
`;

const ChangePlanCoinsText = styled.div`
  line-height: 130%;
`;

export default ({ data, stripeCheckout, selectedPlan, onGoldPlanSelect }) => {
  const [searchParams] = useSearchParams();

  const [failedBox, setFailedBox] = useState(false);

  useEffect(() => {
    const failed = searchParams.get("canceled") === "true";

    if (failed) setFailedBox(true);
  }, []);

  const closeMessageBox = () => {
    setFailedBox(false);
  };

  const mobileScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchMobile})`,
  });

  const activeAdPlan = data.adPlanBillingData.adPlan;

  let planIcon;
  let planName;
  let planMonthlyCoins;
  let product;
  let planPrice = 0;
  console.log("selectedPlan", selectedPlan);

  switch (true) {
    case selectedPlan === 1:
      planIcon = bronzeCheckmarkIcon;
      planName = "Bronze";
      product = "bronze_ad_plan";
      planMonthlyCoins = 2500;
      break;
    case selectedPlan === 2:
      planIcon = silverCheckmarkIcon;
      planName = "Silver";
      product = "silver_ad_plan";
      planMonthlyCoins = 10000;
      break;
    case selectedPlan === 3:
      planIcon = goldCheckmarkIcon;
      planName = "Gold 20";
      product = "gold_20_ad_plan";
      planMonthlyCoins = 20000;
      break;
    case selectedPlan === 4:
      planIcon = goldCheckmarkIcon;
      planName = "Gold 40";
      product = "gold_40_ad_plan";
      planMonthlyCoins = 40000;
      break;
    case selectedPlan === 5:
      planIcon = goldCheckmarkIcon;
      planName = "Gold 60";
      product = "gold_60_ad_plan";
      planMonthlyCoins = 60000;
      break;
    case selectedPlan === 6:
      planIcon = goldCheckmarkIcon;
      planName = "Gold 80";
      product = "gold_80_ad_plan";
      planMonthlyCoins = 80000;
      break;
    case selectedPlan === 7:
      planIcon = goldCheckmarkIcon;
      planName = "Gold 100";
      product = "gold_100_ad_plan";
      planMonthlyCoins = 100000;
      break;
  }

  for (const price of data.adPlanBillingData.prices) {
    if (price.product === product) planPrice = price.price / 100;
  }

  let vatInfo = "";

  if (data?.adPlanBillingData.vatRate) {
    vatInfo = ` (+ ${data.adPlanBillingData.vatRate}% tax)`;
  }

  let activePlanIcon;
  let activePlanName;
  let activePlanMonthlyCoins;
  let daysRemaining = 0;
  let compensation;

  if (activeAdPlan > 0) {
    switch (true) {
      case activeAdPlan === 1:
        activePlanIcon = bronzeCheckmarkIcon;
        activePlanName = "Bronze";
        activePlanMonthlyCoins = 2500;
        break;
      case activeAdPlan === 2:
        activePlanIcon = silverCheckmarkIcon;
        activePlanName = "Silver";
        activePlanMonthlyCoins = 10000;
        break;
      case activeAdPlan === 3:
        activePlanIcon = goldCheckmarkIcon;
        activePlanName = "Gold 20";
        activePlanMonthlyCoins = 20000;
        break;
      case activeAdPlan === 4:
        activePlanIcon = goldCheckmarkIcon;
        activePlanName = "Gold 40";
        activePlanMonthlyCoins = 40000;
        break;
      case activeAdPlan === 5:
        activePlanIcon = goldCheckmarkIcon;
        activePlanName = "Gold 60";
        activePlanMonthlyCoins = 60000;
        break;
      case activeAdPlan === 6:
        activePlanIcon = goldCheckmarkIcon;
        activePlanName = "Gold 80";
        activePlanMonthlyCoins = 80000;
        break;
      case activeAdPlan === 7:
        activePlanIcon = goldCheckmarkIcon;
        activePlanName = "Gold 100";
        activePlanMonthlyCoins = 100000;
        break;
    }

    daysRemaining = daysUntil(data.adPlanBillingData.subscription?.expires) - 1;

    compensation = daysRemaining * activePlanMonthlyCoins;
  }

  const CoinsSelectionOptionDesktop = ({ plan, coins }) => {
    return (
      <CoinsSelectionOption onClick={() => onGoldPlanSelect(plan)}>
        <CoinsSelectionOptionValue>
          <CoinsSelectionOptionIcon>
            <img src={coinsIcon} style={{ width: "100%" }} />
          </CoinsSelectionOptionIcon>
          <CoinsSelectionOptionText selected={selectedPlan === plan}>
            {numberWithCommas(coins)}
          </CoinsSelectionOptionText>
        </CoinsSelectionOptionValue>
        <CoinsSelectionCircleBox>
          <CoinsSelectionCircleBoxBar
            filled={selectedPlan > plan - 1}
            transparent={plan === 3 ? 1 : 0}
          />
          <CoinsSelectionOptionCircle
            filled={selectedPlan > plan}
            selected={selectedPlan === plan}
          >
            <CoinsSelectionOptionInnerCircle filled={selectedPlan >= plan} />
          </CoinsSelectionOptionCircle>
          <CoinsSelectionCircleBoxBar
            filled={selectedPlan > plan}
            transparent={plan === 7 ? 1 : 0}
          />
        </CoinsSelectionCircleBox>
      </CoinsSelectionOption>
    );
  };

  const CoinsSelectionOptionMobile = ({ plan, coins }) => {
    return (
      <CoinsSelectionOption onClick={() => onGoldPlanSelect(plan)}>
        <CoinsSelectionOptionValue>
          <CoinsSelectionOptionIcon>
            <img src={coinsIcon} style={{ width: "100%" }} />
          </CoinsSelectionOptionIcon>
          <CoinsSelectionOptionText selected={selectedPlan === plan}>
            {numberWithCommas(coins)}
          </CoinsSelectionOptionText>
        </CoinsSelectionOptionValue>
        <CoinsSelectionCircleBox>
          <CoinsSelectionCircleBoxBarMobile
            filled={selectedPlan > plan - 1}
            transparent={plan === 3 ? 1 : 0}
          />
          <CoinsSelectionOptionCircle
            filled={selectedPlan > plan}
            selected={selectedPlan === plan}
          >
            <CoinsSelectionOptionInnerCircle filled={selectedPlan >= plan} />
          </CoinsSelectionOptionCircle>
          <CoinsSelectionCircleBoxBarMobile
            filled={selectedPlan > plan}
            transparent={plan === 7 ? 1 : 0}
          />
        </CoinsSelectionCircleBox>
      </CoinsSelectionOption>
    );
  };

  let title = "Review Your Ad Plan";
  if (activeAdPlan === 1 || activeAdPlan === 2) title = "Change Ad Plan";
  if (activeAdPlan >= 3 && selectedPlan >= 3) title = "Adjust Ad Plan";

  return (
    <ReviewAdPlanWrap>
      {failedBox && (
        <MessageBox
          title="Payment Failed"
          text="An error occurred during the payment. Please try again."
          closeBox={closeMessageBox}
          margin="0 0 24px 0"
        />
      )}
      <ReviewAdPlan>
        <BoxTitle>
          <TitleIcon>
            <img src={adPlanIcon} style={{ width: "100%" }} />
          </TitleIcon>
          {title}
        </BoxTitle>
        {activeAdPlan > 0 && (
          <ChangePlanBox>
            <ChangePlanTitle>Ad Plan Change</ChangePlanTitle>
            <Data>
              <DataRow>
                <DataIconBox>
                  <DataIcon>
                    <img src={activePlanIcon} style={{ width: "100%" }} />
                  </DataIcon>
                </DataIconBox>
                <DataText>{activePlanName}</DataText>
                <DataIconBox>
                  <DataIcon>
                    <img src={arrowIcon} style={{ width: "100%" }} />
                  </DataIcon>
                </DataIconBox>
                <DataIconBox>
                  <DataIcon>
                    <img src={planIcon} style={{ width: "100%" }} />
                  </DataIcon>
                </DataIconBox>
                <DataText>{planName}</DataText>
              </DataRow>
            </Data>
            {daysRemaining > 0 && (
              <>
                <ChangePlanText>
                  You are about to change your plan. The coins you are due to
                  receive by the end of the current plan's prepaid period will
                  be immediately credited to you after changing the plan.
                </ChangePlanText>
                <ChangePlanCompensation>
                  <Data>
                    <DataTitle>Current Plan</DataTitle>
                    <DataRow>
                      <DataIconBox>
                        <DataIcon>
                          <img src={coinsIcon} style={{ width: "100%" }} />
                        </DataIcon>
                      </DataIconBox>
                      <DataText>
                        {numberWithCommas(activePlanMonthlyCoins)} Daily
                      </DataText>
                    </DataRow>
                  </Data>
                  <Data>
                    <DataTitle>Days Remaining</DataTitle>
                    <DataRow>
                      <DataIconBox>
                        <DataIcon>
                          <img src={calendarIcon} style={{ width: "100%" }} />
                        </DataIcon>
                      </DataIconBox>
                      <DataText>{daysRemaining}</DataText>
                    </DataRow>
                  </Data>
                </ChangePlanCompensation>
                <ChangePlanText>
                  After completing the plan change, you will receive an
                  additional {numberWithCommas(compensation)} coins.
                </ChangePlanText>
              </>
            )}
          </ChangePlanBox>
        )}
        <Data>
          <DataTitle>Selected Plan</DataTitle>
          <DataRow>
            <DataIconBox>
              <DataIcon>
                <img src={planIcon} style={{ width: "100%" }} />
              </DataIcon>
            </DataIconBox>
            <DataText>{planName}</DataText>
          </DataRow>
        </Data>
        {selectedPlan >= 3 && (
          <GoldPlanCoinsSection>
            <Data>
              <DataTitle>Coins Daily</DataTitle>
              <DataRow>
                <DataText>
                  Choose how many coins you want to receive each day.
                </DataText>
              </DataRow>
            </Data>
            {!mobileScreen && (
              <CoinsSelection>
                <CoinsSelectionOptionDesktop plan={3} coins={20000} />
                <CoinsSelectionBar filled={selectedPlan > 3} />
                <CoinsSelectionOptionDesktop plan={4} coins={40000} />
                <CoinsSelectionBar filled={selectedPlan > 4} />
                <CoinsSelectionOptionDesktop plan={5} coins={60000} />
                <CoinsSelectionBar filled={selectedPlan > 5} />
                <CoinsSelectionOptionDesktop plan={6} coins={80000} />
                <CoinsSelectionBar filled={selectedPlan > 6} />
                <CoinsSelectionOptionDesktop plan={7} coins={100000} />
              </CoinsSelection>
            )}
            {mobileScreen && (
              <CoinsSelection>
                <CoinsSelectionOptionMobile plan={3} coins={20000} />
                <CoinsSelectionOptionMobile plan={4} coins={40000} />
                <CoinsSelectionOptionMobile plan={5} coins={60000} />
                <CoinsSelectionOptionMobile plan={6} coins={80000} />
                <CoinsSelectionOptionMobile plan={7} coins={100000} />
              </CoinsSelection>
            )}
          </GoldPlanCoinsSection>
        )}
        <Features>
          <FeaturesTitle>Features</FeaturesTitle>
          {selectedPlan === 1 && (
            <FeaturesList>
              <Feature>
                <FeatureIcon>
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText>
                  <Bold>2,500 Coins</Bold> added to your account every day
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon translate="2px">
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText translate="-2px">
                  <Bold>Bronze checkmark</Bold>
                  <FeatureBadgeIcon>
                    <img src={bronzeCheckmarkIcon} style={{ width: "100%" }} />
                  </FeatureBadgeIcon>
                  displayed next to your name
                </FeatureText>
              </Feature>
            </FeaturesList>
          )}
          {selectedPlan === 2 && (
            <FeaturesList>
              <Feature>
                <FeatureIcon>
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText>
                  <Bold>10,000 Coins</Bold> added to your account every day
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon translate="2px">
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText translate="-2px">
                  <Bold>Silver checkmark</Bold>
                  <FeatureBadgeIcon>
                    <img src={silverCheckmarkIcon} style={{ width: "100%" }} />
                  </FeatureBadgeIcon>
                  displayed next to your name
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText>
                  Ability to insert a <Bold>Video</Bold> into your post
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText>
                  Increased limit on initiated <Bold>Chat conversations</Bold>{" "}
                  to 6 per day
                </FeatureText>
              </Feature>
            </FeaturesList>
          )}
          {selectedPlan >= 3 && (
            <FeaturesList>
              <Feature>
                <FeatureIcon>
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText>
                  <Bold>{numberWithCommas(planMonthlyCoins)} Coins</Bold> added
                  to your account every day
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon translate="2px">
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText translate="-2px">
                  <Bold>Gold checkmark</Bold>
                  <FeatureBadgeIcon>
                    <img src={goldCheckmarkIcon} style={{ width: "100%" }} />
                  </FeatureBadgeIcon>
                  displayed next to your name
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText>
                  Ability to insert a <Bold>Video</Bold> into your post
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText>
                  Increased limit on initiated <Bold>Chat conversations</Bold>{" "}
                  to 10 per day
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText>
                  <Bold>Extended post length</Bold> up to 1,500 characters
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText>
                  <Bold>Highlighted post</Bold> that stands out
                </FeatureText>
              </Feature>
              <Feature>
                <FeatureIcon>
                  <img src={featureTickIcon} style={{ width: "100%" }} />
                </FeatureIcon>
                <FeatureText>
                  <Bold>Sponsored message</Bold> for even greater exposure
                </FeatureText>
              </Feature>
            </FeaturesList>
          )}
        </Features>
        <Data>
          <DataTitle>Price</DataTitle>
          <DataRow>
            <DataText>${planPrice} USD/month</DataText>
          </DataRow>
        </Data>
        <ChargeInfoBox>
          <ChargeInfoBoxIcon>
            <img src={bankCardIcon} style={{ width: "100%" }} />
          </ChargeInfoBoxIcon>
          <ChargeInfoBoxText>
            ${planPrice} USD{vatInfo} will be automatically charged to your card
            each month starting today. You can cancel the subscription at any
            time.
          </ChargeInfoBoxText>
        </ChargeInfoBox>
        <PaymentSection>
          <BoxTitle>Proceed to payment</BoxTitle>
          <Text>
            After clicking the Subscribe button, you will be redirected to the
            payment gateway.
          </Text>
          <PaymentButtonBox>
            <PaymentButtonWrap>
              <PaymentButton
                onClick={() => {
                  stripeCheckout(product);
                }}
                disabled={false}
              >
                <PaymentButtonText>Subscribe</PaymentButtonText>
                <MethodImage>
                  <img
                    src={stripeButtonImage}
                    alt="Pay with Cryptocurrency"
                    style={{ width: "100%" }}
                    height="22"
                  />
                </MethodImage>
              </PaymentButton>
            </PaymentButtonWrap>
            <PaymentText>Powered by Stripe</PaymentText>
          </PaymentButtonBox>
        </PaymentSection>
      </ReviewAdPlan>
    </ReviewAdPlanWrap>
  );
};
